import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { useAuth } from "@/context/AuthContext";
import { Button } from "@/design-system/components/react";
import { ContentCase } from "@/components/contentLibrary/ContentCase";
import {
  contentError,
  importContent,
  listContent,
  permissionLabel,
  formatLabel,
  type ContentCandidate,
} from "@/services/contentLibrary";

function useContentLibrary(format: ContentCandidate["kind"] | "all") {
  const { userId } = useAuth();
  const identity = userId ?? "";
  const [cursor, setCursor] = useState<string | null>(null);
  const [selected, setSelected] = useState<string | null>(null);
  const [file, setFile] = useState<File | null>(null);
  const client = useQueryClient();
  const query = useQuery({
    queryKey: ["content-library", identity, "list", cursor, format],
    queryFn: () => listContent(cursor, format),
    enabled: !!identity,
  });
  const upload = useMutation({
    mutationFn: importContent,
    onSuccess: async (result) => {
      setSelected(result.candidate_ids[0] ?? null);
      setCursor(null);
      setFile(null);
      await client.invalidateQueries({ queryKey: ["content-library", identity] });
    },
  });
  return { identity, cursor, setCursor, selected, setSelected, file, setFile, query, upload };
}

function ImportForm({ state }: { state: ReturnType<typeof useContentLibrary> }) {
  const { file, setFile, upload } = state;
  return (
    <form
      className="flex flex-wrap items-end gap-3"
      onSubmit={(e) => {
        e.preventDefault();
        if (file) upload.mutate(file);
      }}
    >
      <label className="text-sm">
        Candidate bundle
        <input
          className="mt-1 block text-sm"
          type="file"
          accept=".zip,application/zip"
          onChange={(e) => setFile(e.target.files?.[0] ?? null)}
        />
      </label>
      <Button type="submit" disabled={!file || upload.isPending}>
        {upload.isPending ? "Importing…" : "Import candidates"}
      </Button>
      {upload.isError && <p role="alert">{contentError(upload.error)}</p>}
      {upload.isSuccess && (
        <p role="status" className="text-sm">
          Candidates imported.
        </p>
      )}
    </form>
  );
}

export default function ContentLibrary() {
  const [format, setFormat] = useState<ContentCandidate["kind"] | "all">("all");
  const state = useContentLibrary(format);
  const { identity, cursor, setCursor, selected, setSelected, query } = state;
  return (
    <div className="mx-auto max-w-screen-2xl space-y-6 p-4 text-fg md:p-6">
      <header className="space-y-2">
        <h1 className="text-2xl font-semibold">Content Library</h1>
        <p className="max-w-3xl text-sm text-fg-muted">
          Internal candidate preparation. Record supplier permission for the selected images and
          public LinkedIn use. Clinical and Regulatory Science review of final wording is still
          needed before publishing.
        </p>
        <p className="text-sm font-medium">
          One shared internal library. Staging and production use the same content and clearance
          history. Clearance decisions can be changed only in production.
        </p>
      </header>
      <ImportForm state={state} />
      <nav aria-label="Content formats" className="flex flex-wrap gap-2">
        {(["all", "case", "flag_set", "explainer", "poll"] as const).map((kind) => (
          <Button
            key={kind}
            variant={format === kind ? "default" : "outline"}
            aria-pressed={format === kind}
            onClick={() => {
              setFormat(kind);
              setCursor(null);
              setSelected(null);
            }}
          >
            {kind === "all" ? "All formats" : formatLabel[kind]}
          </Button>
        ))}
      </nav>
      <div className="grid gap-6 lg:grid-cols-[260px_minmax(0,1fr)]">
        <aside className="space-y-2" aria-label="Candidates">
          {query.isPending && <p role="status">Loading candidates…</p>}
          {query.isError && <p role="alert">{contentError(query.error)}</p>}
          {query.data?.items.length === 0 && (
            <p className="text-sm text-fg-muted">No content on this page.</p>
          )}
          {query.data?.items.map((record) => (
            <button
              key={record.candidate_id}
              type="button"
              onClick={() => setSelected(record.candidate_id)}
              aria-pressed={selected === record.candidate_id}
              className={`w-full rounded border p-3 text-left ${selected === record.candidate_id ? "border-signal bg-signal-tint" : "border-border-strong bg-bg-2"}`}
            >
              <span className="block text-sm font-medium">{record.candidate.title}</span>
              <span className="block text-xs text-fg-muted">
                {formatLabel[record.candidate.kind]} · {permissionLabel(record)}
              </span>
            </button>
          ))}
          <div className="flex gap-2">
            {cursor && (
              <Button variant="outline" onClick={() => setCursor(null)}>
                First page
              </Button>
            )}
            {query.data?.next_cursor && (
              <Button variant="outline" onClick={() => setCursor(query.data!.next_cursor)}>
                Next page
              </Button>
            )}
          </div>
        </aside>
        <section className="min-w-0 rounded border border-border-strong bg-bg-2 p-4">
          {selected ? (
            <ContentCase key={selected} id={selected} userId={identity} />
          ) : (
            <p className="text-sm text-fg-muted">
              Select content to preview its media, review draft text, and track supplier permission
              for clinical cases.
            </p>
          )}
        </section>
      </div>
    </div>
  );
}
